import { type FunctionComponent } from 'react';
import { buildGTMSblViewCompleteLook } from '../../helpers/analytics/gtm/event-builders';
import { type CloudinaryOptions } from '../../helpers/cloudinary-helper/cloudinary-helper';
import { type QuickStartImageGridLook } from '../../types/project.types';
import { Link } from '../common-components/link/link.component';
import { StyledLink } from '../common-components/link/styled-link.component';
import { CloudinaryImage } from '../images/cloudinary-image/cloudinary-image.component';
import { QuickStartShowcaseFavoriteButton } from '../quickstart-showcase-favorite-button/quickstart-showcase-favorite-button.component';
import { shopByLookPageImages } from './quickstart-image-grid.css';

export type QuickStartImageGridProps = {
	looks: QuickStartImageGridLook[];
	containerClassName?: string;
	className?: string;
	showTitle?: boolean;
	isCarousel?: boolean;
	isShopByLookPage?: boolean;
	isShopByLookPdp?: boolean;
	keyPrefix?: string;
	cloudinaryOptions?: CloudinaryOptions;
	fetchMoreLooks?: () => void;
};

export const QuickStartImageGrid: FunctionComponent<QuickStartImageGridProps> = ({
	looks,
	containerClassName = 'flex-l flex-wrap flex-row',
	className = 'mv3 ph3-l',
	showTitle = true,
	isCarousel = false,
	isShopByLookPage = false,
	isShopByLookPdp = false,
	keyPrefix = '',
	cloudinaryOptions
}) => {
	className += looks.length === 1 && !isCarousel ? ' w-100-l' : isCarousel ? '' : ' w-50-l';

	return (
		<div className={containerClassName} data-testid="quickstart-image-grid">
			{looks.map(({ imageId, title, url, quickStartId }, index) => {
				return (
					<QuickStartImage
						key={`${keyPrefix}-${title}-${index}`}
						className={className}
						imageId={imageId}
						title={showTitle ? title : ''}
						url={url}
						quickStartId={quickStartId}
						isShopByLookPage={isShopByLookPage}
						isShopByLookPdp={isShopByLookPdp}
						cloudinaryOptions={cloudinaryOptions}
						isCarousel={isCarousel}
					/>
				);
			})}
		</div>
	);
};

export type QuickStartImageProps = QuickStartImageGridLook & {
	className: string;
	isShopByLookPage?: boolean;
	isShopByLookPdp?: boolean;
	cloudinaryOptions?: CloudinaryOptions;
	isCarousel?: boolean;
};

export const QuickStartImage: FunctionComponent<QuickStartImageProps> = ({
	className,
	imageId,
	title,
	url,
	quickStartId,
	isShopByLookPage = false,
	isShopByLookPdp = false,
	cloudinaryOptions = {}
}) => {
	return (
		<div className={className} data-testid="quickstart-image">
			<div className={`relative ${isShopByLookPage ? shopByLookPageImages : ''}`}>
				<Link url={url} analyticsEvent={isShopByLookPdp ? { data: buildGTMSblViewCompleteLook() } : []}>
					<CloudinaryImage
						options={{ type: 'upload', ...cloudinaryOptions }}
						publicID={imageId}
						description={`A photo of a ${title}`}
						className="br3 w-100"
					/>
					<QuickStartShowcaseFavoriteButton isShopByLook={isShopByLookPage} quickStartId={quickStartId} />
				</Link>
			</div>
			{title && (
				<StyledLink
					url={url}
					analyticsEvent={isShopByLookPdp ? { data: buildGTMSblViewCompleteLook() } : []}
					color="grey-darker"
					className="db mt3">
					{title}
				</StyledLink>
			)}
		</div>
	);
};
